import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import StatsCard from '../../components/Admin/StatsCard';
import './Dashboard.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001';
const API_VERSION = process.env.REACT_APP_API_VERSION || 'v1';

const AdminDashboard = () => {
  const [stats, setStats] = useState(null);
  const [recentActivity, setRecentActivity] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      
      if (!token) {
        console.error('No admin token found');
        window.location.href = '/admin/login';
        return;
      }

      const config = {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      };

      console.log('Fetching dashboard data from:', `${API_URL}/api/${API_VERSION}/admin/dashboard/stats`);

      const [statsRes, activityRes] = await Promise.all([
        axios.get(`${API_URL}/api/${API_VERSION}/admin/dashboard/stats`, config),
        axios.get(`${API_URL}/api/${API_VERSION}/admin/dashboard/recent-activity`, config)
      ]);

      console.log('Stats response:', statsRes.data);
      console.log('Activity response:', activityRes.data);

      setStats(statsRes.data.stats);
      setRecentActivity(activityRes.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      console.error('Error response:', error.response);
      console.error('Error message:', error.message);
      
      if (error.response?.status === 401 || error.response?.status === 403) {
        localStorage.removeItem('adminToken');
        localStorage.removeItem('adminUser');
        window.location.href = '/admin/login';
      }
      
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="admin-dashboard-loading">
        <i className="fas fa-spinner fa-spin"></i>
        <p>Loading dashboard...</p>
      </div>
    );
  }

  return (
    <div className="admin-dashboard">
      <div className="dashboard-header">
        <h1>Dashboard Overview</h1>
        <p>Welcome to the admin portal</p>
      </div>

      {/* Stats Grid */}
      <div className="stats-grid">
        <StatsCard
          title="Total Organizations"
          value={stats?.organizations?.total || 0}
          subtitle={`${stats?.organizations?.active || 0} active`}
          icon="fas fa-building"
          color="blue"
          trend={`+${stats?.organizations?.newThisMonth || 0} this month`}
        />
        <StatsCard
          title="Total Users"
          value={stats?.users?.total || 0}
          subtitle={`${stats?.users?.active || 0} active`}
          icon="fas fa-users"
          color="green"
          trend={`+${stats?.users?.newThisWeek || 0} this week`}
        />
        <StatsCard
          title="Demo Requests"
          value={stats?.demoRequests?.total || 0}
          subtitle={`${stats?.demoRequests?.pending || 0} pending`}
          icon="fas fa-calendar-check"
          color="orange"
          trend={`${stats?.demoRequests?.confirmed || 0} confirmed`}
        />
        <StatsCard
          title="Active Subscriptions"
          value={stats?.subscriptions?.active || 0}
          subtitle={`${stats?.subscriptions?.trial || 0} on trial`}
          icon="fas fa-box"
          color="purple"
          trend={`${stats?.subscriptions?.total || 0} total`}
        />
        <StatsCard
          title="Total Revenue"
          value={`$${stats?.revenue?.total || 0}`}
          subtitle="All time"
          icon="fas fa-dollar-sign"
          color="green"
          trend={`$${stats?.revenue?.monthly || 0} this month`}
        />
        <StatsCard
          title="Active Vendors"
          value={stats?.vendors?.active || 0}
          subtitle={`${stats?.vendors?.total || 0} total`}
          icon="fas fa-handshake"
          color="indigo"
          trend="Vendor management"
        />
        <StatsCard
          title="Active Projects"
          value={stats?.projects?.active || 0}
          subtitle={`${stats?.projects?.total || 0} total`}
          icon="fas fa-project-diagram"
          color="teal"
          trend="Project tracking"
        />
        <StatsCard
          title="Conversion Rate"
          value={`${stats?.metrics?.conversionRate || 0}%`}
          subtitle="Demo to paid"
          icon="fas fa-chart-line"
          color="pink"
          trend="Performance metric"
        />
      </div>

      {/* Recent Activity */}
      <div className="dashboard-section">
        <h2>Recent Activity</h2>
        
        <div className="activity-grid">
          {/* Recent Demo Requests */}
          <div className="activity-card">
            <div className="activity-card-header">
              <h3>Recent Demo Requests</h3>
              <Link to="/admin/demo-requests" className="view-all-link">
                View All <i className="fas fa-arrow-right"></i>
              </Link>
            </div>
            <div className="activity-list">
              {recentActivity?.recentDemoRequests?.map((demo) => (
                <div key={demo.id} className="activity-item">
                  <div className="activity-icon">
                    <i className="fas fa-calendar-check"></i>
                  </div>
                  <div className="activity-content">
                    <p className="activity-title">
                      {demo.firstName} {demo.lastName} - {demo.company}
                    </p>
                    <p className="activity-meta">
                      <span className={`status-badge status-${demo.status}`}>
                        {demo.status}
                      </span>
                      <span>{new Date(demo.created_at).toLocaleDateString()}</span>
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Recent Organizations */}
          <div className="activity-card">
            <div className="activity-card-header">
              <h3>New Organizations</h3>
              <Link to="/admin/organizations" className="view-all-link">
                View All <i className="fas fa-arrow-right"></i>
              </Link>
            </div>
            <div className="activity-list">
              {recentActivity?.recentOrganizations?.map((org) => (
                <div key={org.id} className="activity-item">
                  <div className="activity-icon">
                    <i className="fas fa-building"></i>
                  </div>
                  <div className="activity-content">
                    <p className="activity-title">{org.name}</p>
                    <p className="activity-meta">
                      {new Date(org.created_at).toLocaleDateString()}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;

